import * as Sentry from '@sentry/react';

interface SentryConfig {
  dsn?: string;
  environment: string;
  enabled: boolean;
}

function getSentryConfig(): SentryConfig {
  const dsn =
    typeof import.meta.env['VITE_SENTRY_DSN'] === 'string'
      ? import.meta.env['VITE_SENTRY_DSN']
      : undefined;

  const environment = import.meta.env.MODE || 'development';
  const enabled = Boolean(dsn && dsn.trim() !== '');

  return {
    ...(dsn !== undefined ? { dsn } : {}),
    environment,
    enabled,
  };
}

export function initSentry(): void {
  const config = getSentryConfig();

  if (!config.enabled) {
    return;
  }

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: config.environment === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      const error = hint.originalException;

      if (error instanceof Error && error.message.includes('ResizeObserver')) {
        return null;
      }

      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
        delete event.request.headers['Cookie'];
      }

      return event;
    },
  });
}

export function setUserContext(user: { id: string; email?: string }): void {
  Sentry.setUser({
    id: user.id,
    ...(user.email !== undefined ? { email: user.email } : {}),
  });
}

export function clearUserContext(): void {
  Sentry.setUser(null);
}

export function captureError(error: Error, context?: Record<string, unknown>): void {
  Sentry.captureException(
    error,
    context !== undefined
      ? {
          extra: context,
        }
      : undefined
  );
}
